import React from "react";
import axios from "axios";
import { API_get_plan_task_analytics_overview } from "../../../../constants";
import { getDefaultAxiosConfig } from "../../../../utilities/requests";
import LoadSpinner from "../../../../utilities/LoadSpinner";
import { formatDateYMD, addDays } from "./taskHubUtils";
import {
  SummaryTiles,
  StackedTrend,
  OutcomeBar,
  EmployeeTable,
  StoreTable,
  pct,
} from "./reportComponents";

// Reports level 1: the scoreboard. Answers "is task work happening, and where
// is it breaking down" across every running task, worst offenders first.
// Clicking a task drills into its detail (level 2).

const RANGES = [
  ["7", "Last 7 days"],
  ["14", "Last 14 days"],
  ["30", "Last 30 days"],
];

function TaskReports({ homeCompany, homeStore, onPickTask }) {
  const [rangeDays, setRangeDays] = React.useState("14");
  const [allLocations, setAllLocations] = React.useState(false);
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  // A failed request is not an empty range: saying so would tell the manager
  // their tasks produced nothing.
  const [error, setError] = React.useState(false);
  const [reloadKey, setReloadKey] = React.useState(0);

  const dateEnd = formatDateYMD(new Date());
  const dateStart = formatDateYMD(addDays(new Date(), -(parseInt(rangeDays, 10) - 1)));

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const axiosConfig = await getDefaultAxiosConfig();
        const storeParam = allLocations ? "" : `&store_ids=${homeStore}`;
        const res = await axios.get(
          `${API_get_plan_task_analytics_overview}?company_id=${homeCompany}&date_start=${dateStart}&date_end=${dateEnd}${storeParam}`,
          axiosConfig
        );
        if (!cancelled) {
          setData(res.data?.redux?.payload || null);
          setError(false);
        }
      } catch (e) {
        if (!cancelled) {
          setData(null);
          setError(true);
        }
        console.error("Error fetching task reports:", e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [homeCompany, homeStore, rangeDays, allLocations, dateStart, dateEnd, reloadKey]);

  const scope = { dateStart, dateEnd, allLocations };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <LoadSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <ScopeBar
          rangeDays={rangeDays}
          setRangeDays={setRangeDays}
          allLocations={allLocations}
          setAllLocations={setAllLocations}
        />
        <div className="border border-slate-200 rounded-xl bg-white p-10 text-center mt-4">
          <div className="text-sm font-semibold text-slate-700">
            These reports could not be loaded
          </div>
          <button
            onClick={() => setReloadKey((k) => k + 1)}
            className="text-sm font-semibold text-violet-700 hover:text-violet-800 mt-2"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (!data || !data.summary || data.summary.expected === 0) {
    return (
      <div>
        <ScopeBar
          rangeDays={rangeDays}
          setRangeDays={setRangeDays}
          allLocations={allLocations}
          setAllLocations={setAllLocations}
        />
        <div className="border border-slate-200 rounded-xl bg-white p-10 text-center mt-4">
          <div className="text-sm font-semibold text-slate-700">
            Nothing to report in this range
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Reports build up once tasks are activated and start running. Try a
            different range, or activate tasks in the Library tab.
          </div>
        </div>
      </div>
    );
  }

  const tasks = data.tasks || [];
  const problemTasks = tasks.filter((t) => t.missed > 0).length;

  return (
    <div>
      <ScopeBar
        rangeDays={rangeDays}
        setRangeDays={setRangeDays}
        allLocations={allLocations}
        setAllLocations={setAllLocations}
      />

      <div className="mt-4">
        <SummaryTiles summary={data.summary} />
      </div>

      <div className="mt-4">
        <StackedTrend dailyTrend={data.daily_trend} title="Daily outcomes" />
      </div>

      <div className="grid grid-cols-[2fr_1fr] sm:grid-cols-1 gap-4 mt-4 items-start">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 flex items-baseline gap-2">
            <span className="text-sm font-bold text-slate-900">By task</span>
            <span className="text-[11px] text-slate-400">
              {problemTasks > 0
                ? `${problemTasks} with problems, worst first`
                : "everything is on track"}
            </span>
          </div>
          <div className="grid grid-cols-[1fr_140px_70px_70px_80px] sm:grid-cols-[1fr_80px] gap-3 items-center px-4 py-2 border-b border-slate-100 text-[10px] font-bold uppercase tracking-wider text-slate-400">
            <span>Task</span>
            <span className="sm:hidden">Outcomes</span>
            <span className="text-right sm:hidden">On time</span>
            <span className="text-right">Missed</span>
            <span className="text-right sm:hidden">Median</span>
          </div>
          {tasks.map((t) => (
            <button
              key={t.uuid}
              onClick={() => onPickTask(t, scope)}
              className="w-full text-left grid grid-cols-[1fr_140px_70px_70px_80px] sm:grid-cols-[1fr_80px] gap-3 items-center px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-500"
            >
              <span className="min-w-0">
                <span className="text-sm font-semibold text-slate-900 truncate block">
                  {t.name}
                </span>
                <span className="text-[11px] text-slate-400">
                  {[
                    t.category,
                    t.is_archived ? "Deleted" : t.status === "paused" ? "Paused" : null,
                    t.scoring_enabled
                      ? t.avg_percent == null
                        ? "Scored audit"
                        : `Avg score ${t.avg_percent}%${t.lowest_band_count ? ` · ${t.lowest_band_count} to re-audit` : ""}`
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || `${t.expected} expected`}
                </span>

              </span>
              <span className="sm:hidden">
                <OutcomeBar row={t} />
              </span>
              <span className="text-right text-xs font-semibold text-emerald-700 tabular-nums sm:hidden">
                {pct(t.on_time_rate)}
              </span>
              <span
                className={`text-right text-xs font-semibold tabular-nums ${
                  t.missed > 0 ? "text-rose-600" : "text-slate-400"
                }`}
              >
                {t.missed}
              </span>
              <span className="text-right text-xs text-slate-500 tabular-nums sm:hidden">
                {t.track_time && t.median_minutes != null ? `${t.median_minutes}m` : "–"}
              </span>
            </button>
          ))}
        </div>

        <div className="flex flex-col gap-4">
          <EmployeeTable rows={data.employee_breakdown} title="Top completers" />
          <StoreTable rows={data.store_breakdown} />
        </div>
      </div>
    </div>
  );
}

function ScopeBar({ rangeDays, setRangeDays, allLocations, setAllLocations }) {
  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="inline-flex rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        {RANGES.map(([val, label]) => (
          <button
            key={val}
            onClick={() => setRangeDays(val)}
            className={`px-3 py-1.5 text-xs font-semibold ${
              rangeDays === val
                ? "bg-violet-700 text-white"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="inline-flex rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        {[
          [false, "This location"],
          [true, "All locations"],
        ].map(([val, label]) => (
          <button
            key={label}
            onClick={() => setAllLocations(val)}
            className={`px-3 py-1.5 text-xs font-semibold ${
              allLocations === val
                ? "bg-violet-700 text-white"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}

export default TaskReports;
